import { Formik, useFormikContext } from 'formik'
import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import Form from '../../Form'
import {
    FilePartyMetaDataDto,
    InvoiceDto,
    InvoiceMutationDto,
    InvoiceStatus,
    InvoiceTemplateDto,
    InvoiceType,
    LineItemType,
    MetaDataDto,
} from '../../../../config/finance/invoiceConfig'
import HeaderCard from './HeaderCard'
import { FormikConfig } from 'formik/dist/types'

import InvoiceFieldSets from './InvoiceFieldsets'
import FormContainer from '../../../../components/Form/FormContainer'
import InvoiceParties from './InvoiceParties'
import Loading from '../../../../components/Loading'
import { DataPromise } from '../../../../context/AxiosContext'
import useLineItemEntities, { LineItemEntities } from '../../../../hooks/finance/invoice/useLineItemEntities'
import useEntity from '../../../../hooks/useEntity'
import InvoiceFile from './InvoiceFile'
import { Button, makeStyles, mergeClasses, MessageBar, MessageBarBody, shorthands, tokens } from '@fluentui/react-components'
import * as Yup from 'yup'
import usePersistInvoice, { PersistInvoice } from '../../../../hooks/finance/invoice/usePersistInvoice'
import { DocumentGenerationType } from '../../../../hooks/document/useDocumentGenerationHandler'
import useSubmitInvoiceDocumentGeneration, {
    SubmitInvoiceDocumentGeneration,
} from '../../../../hooks/finance/invoice/useSubmitInvoiceDocumentGeneration'
import useVatTypes from '../../../../hooks/useVatTypes'
import { useTranslation } from 'react-i18next'
import { formatMultipleEmailAddresses, yupEmailRegex } from '../../../../helpers/regexHelper'

type Props = {
    formikProps: FormikConfig<InvoiceMutationDto>
    invoice?: InvoiceDto
    templates: InvoiceTemplateDto[]
}

export type InvoiceProps = Omit<Props, 'formikProps'> & {
    meta: DataPromise<MetaDataDto>
    useInitialInvoice: boolean
    useInitialValues: boolean
    hasParties: boolean
    hasMultipleFiles: boolean
    isEditable: boolean
    isFinal: boolean
    defaultVatType: string
    lineItemEntities: LineItemEntities
    lineItemGroupPrices: Map<string, PriceVat>
    persist: PersistInvoice
    generatePdf: SubmitInvoiceDocumentGeneration
}

export type PriceVat = {
    subtotal: number
    vat?: Vat[]
    total: number
}

export type Vat = {
    value: number
    type: string
}

const requiresType =
    () =>
    ([type], schema) => {
        return type && [InvoiceType.Split].includes(type) ? schema.nullable() : schema.required()
    }

const getValidationSchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        number: Yup.string().required().label(translate('invoice.edit.form.header.number')),
        debtorPivotId: Yup.string().label(translate('invoice.debtorpivotid')).when('type', requiresType()),
        clientPivotId: Yup.string().label(translate('invoice.clientpivotid')).when('type', requiresType()),
        recipientReference: Yup.string()
            .label(translate('invoice.edit.form.header.recipientreference.label'))
            .test({
                message: translate('invoice.edit.form.header.recipientreference.required'),
                test: (value, context) => {
                    const references = value?.trim()?.split(',')
                    const fileIds = context.parent.fileIds?.length

                    return context.parent.type === 'SPLIT' || !context.parent.recipientReferenceRequired || references?.length >= fileIds
                },
            }),
        recipientEmail: Yup.string()
            .label(translate('invoice.edit.form.header.recipientemail'))
            .test({
                message: translate('base.form.email.warning'),
                test: values =>
                    !values ||
                    formatMultipleEmailAddresses(values)?.every(value => {
                        const result = value?.trim().match(yupEmailRegex)
                        return result?.length > 0
                    }),
            }),
        children: Yup.array().of(
            Yup.object().shape({
                debtorPivotId: Yup.string().label(translate('invoice.debtorpivotid')).required(),
                clientPivotId: Yup.string().label(translate('invoice.clientpivotid')).required(),
                recipientReference: Yup.string()
                    .label(translate('invoice.edit.form.header.recipientreference.label'))
                    .nullable()
                    .test({
                        message: translate('invoice.edit.form.header.recipientreference.required.split'),
                        test: (value, context) => {
                            const references = value?.trim()?.split(',')
                            const fileIds = context.parent.fileIds?.length

                            return !context.parent.recipientReferenceRequired || references?.length >= fileIds
                        },
                    }),
                factor: Yup.number().required().label(translate('invoice.edit.form.header.debtorfactor')),
            }),
        ),
    })
}

export const getFilePartyFromMeta = (
    filePartyId: string,
    role: string,
    partyId: string,
    fileParties: FilePartyMetaDataDto[] | undefined,
): FilePartyMetaDataDto[] =>
    fileParties
        .filter(fileParty => !role || fileParty.role === role)
        .filter(fileParty => fileParty.pivotId === filePartyId || fileParty.id === partyId)

const useStyles = makeStyles({
    container: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
})

function Container({ invoice, templates }: Props): ReactElement {
    const { t: translate } = useTranslation()
    const styles = useStyles()

    const { initialValues, values, status, setStatus, isSubmitting, setFieldValue } = useFormikContext<InvoiceMutationDto>()
    // Use the initial invoice data instead of the metadata info when the file IDs value is equal to its original value.
    const useInitialInvoice = useMemo<boolean>(
        () => invoice && invoice?.files.filter(file => !(values.fileIds || []).includes(file.id)).length === 0,
        [values.fileIds],
    )
    // Use the initial values instead of the template / metadata info.
    const useInitialValues = useMemo<boolean>(() => initialValues?.lineItemGroups?.length > 0, [values.fileIds])
    //Update metadata whenever the number of file ids change.
    const [meta] = useEntity<MetaDataDto>(
        () => ({
            url:
                '/finance/invoice/meta/' +
                (useInitialInvoice ? invoice.id : 'file') +
                '?' +
                [values.fileIds.map(id => `fileId=${id}`).join('&'), values.type ? `type=${values.type}` : ''] //
                    .filter(Boolean)
                    .join('&'),
        }),
        values.type && values.fileIds?.length > 0 ? `${values.fileIds?.length}-${values.type}` : null,
    )

    const { getVatType, defaultVatType } = useVatTypes()
    const hasParties = useMemo<boolean>(() => meta.data?.files?.filter(file => file.fileParties?.length > 0).length > 0, [meta.data])
    const hasMultipleFiles = useMemo(() => meta.data?.files?.length > 1, [meta.data])

    const isEndUserInvoice = useMemo<boolean>(() => ![InvoiceType.Split].includes(values.type), [values.type])
    const isSplitInvoice = useMemo<boolean>(() => [InvoiceType.Split].includes(values.type), [values.type])

    const lineItemEntities = useLineItemEntities(meta, values, invoice)

    const persist = usePersistInvoice()
    const generatePdf = useSubmitInvoiceDocumentGeneration(DocumentGenerationType.Invoice)

    const lineItemGroupTriggers = useMemo(
        () =>
            JSON.stringify(
                values.lineItemGroups.map(lineItemGroup => ({
                    id: lineItemGroup.id,
                    precedence: lineItemGroup.precedence,
                    price: lineItemGroup.price,
                    vat: lineItemGroup.vatType,
                    entityIds: lineItemGroup.lineItems.map(lineItem => lineItem.entityId),
                })),
            ),
        [values.lineItemGroups, lineItemEntities.entityMap, lineItemEntities.toSettlePrice],
    )
    const lineItemGroupPrices = useMemo<Map<string, PriceVat>>(
        () =>
            new Map(
                values.lineItemGroups.map(lineItemGroup => [
                    lineItemGroup.id,
                    lineItemEntities.getGroupPrice(
                        lineItemGroup,
                        getVatType(
                            lineItemGroup.vatType ||
                                meta.data?.files?.find(file => file.file.id === lineItemGroup.fileId)?.file.vatType ||
                                defaultVatType?.id,
                        ),
                    ),
                ]),
            ),
        [lineItemGroupTriggers, meta.data],
    )

    const props: InvoiceProps = {
        invoice,
        templates,
        meta,
        hasParties,
        hasMultipleFiles,
        isEditable: [InvoiceStatus.New, InvoiceStatus.Draft].includes(values.status),
        isFinal: [InvoiceStatus.Final, InvoiceStatus.Settled, InvoiceStatus.Credited].includes(values.status),
        useInitialInvoice,
        useInitialValues,
        lineItemEntities,
        lineItemGroupPrices,
        defaultVatType: meta.data?.files?.[0]?.file.vatType,
        persist,
        generatePdf,
    }

    useEffect(() => {
        if (isSplitInvoice) {
            setFieldValue('clientPivotId', undefined)
            setFieldValue('debtorPivotId', undefined)
            setFieldValue('recipientEmail', undefined)
            setFieldValue('recipientReference', undefined)
            setFieldValue('recipientReferenceRequired', false)
            setFieldValue('messageSubject', undefined)
            setFieldValue('messageBody', undefined)
            setFieldValue('paymentMessageBody', undefined)
            setFieldValue('children', [])
        } else {
            setFieldValue('children', [])
        }
    }, [values.type, isSplitInvoice])

    useEffect(() => {
        // Make sure to clear out any status messages after we're done.
        if (!isSubmitting) {
            setStatus(undefined)
        }
    }, [isSubmitting])

    return (
        <Form status={status} noLoading={status === undefined} className={mergeClasses(styles.container)}>
            {meta.loaded && !meta.loading && <HeaderCard {...props} />}
            <FormContainer fullWidth>
                {values.type !== InvoiceType.Refund && lineItemEntities.selection.get(LineItemType.Invoice)?.length > 0 && (
                    <MessageBar intent={'info'} layout={'multiline'}>
                        <MessageBarBody>{translate('invoice.edit.info.settling')}</MessageBarBody>
                    </MessageBar>
                )}
                <InvoiceFile {...props} />
                {meta.loading && <Loading label={translate('invoice.edit.loading.file')} />}
                {isEndUserInvoice && meta.data && <InvoiceParties {...props} />}
                {meta.data && <InvoiceFieldSets {...props} />}
            </FormContainer>
        </Form>
    )
}

export default function InvoiceForm(props: Omit<Props, 'templates'>): ReactElement {
    const { t: translate } = useTranslation()
    const [templates] = useEntity<InvoiceTemplateDto[]>(() => ({ url: '/finance/invoice/template' }), false)

    if (templates.loading) {
        return <Loading label={translate('invoice.edit.loading.templates')} />
    }
    return (
        <>
            {props.invoice?.credit?.length > 0 && (
                <MessageBar intent={'info'}>
                    <MessageBarBody>
                        {translate('invoice.edit.info.credit')}
                        <>
                            {props.invoice.credit?.map((item, index) => (
                                <Button key={index} size={'small'}>
                                    <Link key={index} to={`/invoice/${item.id}/edit`}>
                                        {item.number}
                                    </Link>
                                </Button>
                            ))}
                        </>
                    </MessageBarBody>
                </MessageBar>
            )}
            <Formik<InvoiceMutationDto>
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={getValidationSchema(translate)}
                {...props.formikProps}
            >
                <Container {...props} templates={templates.data} />
            </Formik>
        </>
    )
}
